/* eslint-disable @stencil-community/own-props-must-be-private */
/* STENCIL IMPORTS */
import {
  Component,
  Element,
  Host,
  Listen,
  Method,
  Prop,
  State,
  Watch,
  h
} from "@stencil/core";
/* OTHER LIBRARIES IMPORTS */
import {
  AccordionModel,
  ChGridRowContextMenuEvent,
  ComboBoxModel
} from "@genexus/chameleon-controls-library";
import { getIconPath, MercuryBundles } from "@genexus/mercury";
/* CUSTOM IMPORTS */
import { FILTER_AFTER } from "../../common/constants";
import { config } from "../../common/config";
import { formatDate } from "../../common/helpers";
import { Locale } from "../../common/locale";
import { ContextMenuInfo, EntityData, GxOption } from "../../common/types";
import { mapOptionsToComboBoxItemModel } from "./helpers";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/accordion",
  "components/button",
  "components/checkbox",
  "components/combo-box",
  "components/edit",
  "components/icon",
  "components/tabular-grid",
  "components/sidebar",
  "utils/form",
  "utils/layout",
  "utils/typography",
  "utils/spacing",
  "chameleon/scrollbar"
];

const ALL_DESCENDANTS_VALUE = "all-descendants";
const MODIFIED_VALUE = "modified";

let TODAY: string = null;

// icons
const RESET_ICON = getIconPath({
  category: "gemini-tools",
  name: "reset",
  colorType: "interactive"
});
const SEARCH_ICON = getIconPath({
  category: "gemini-tools",
  name: "search",
  colorType: "neutral"
});
const NOTICE_ICON = getIconPath({
  category: "gemini-tools",
  name: "notice",
  colorType: "on-elevation"
});

@Component({
  tag: "gx-ide-ww-images",
  styleUrl: "ww-images.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/ww-images"]
})
export class GxIdeWWImages {
  /**
   * The component hard-coded strings translations.
   */
  #componentLocale: any;
  #filterInitialData: FilerInitialData;
  #ACCORDION_MODEL: AccordionModel = [];
  #ACCORDION_FILTER_ID = "advanced-filter";

  #imagesMap: Map<string, ImageItemData[]> = new Map();

  @Element() el: HTMLGxIdeWwImagesElement;

  #chGridEl!: HTMLChTabularGridElement;
  // Only keep refs that are actually needed for operations (event listeners)
  #filterModuleEl!: HTMLGxIdeEntitySelectorElement;

  // representations of GxOption[] as ComboBoxModel.
  @State() categoriesComboBoxModel: ComboBoxModel = [];
  @State() densitiesComboBoxModel: ComboBoxModel = [];
  @State() languagesComboBoxModel: ComboBoxModel = [];
  @State() layersComboBoxModel: ComboBoxModel = [];
  @State() stylesComboBoxModel: ComboBoxModel = [];

  /**
   * The current value of the "After" combo box options.
   */
  @State() afterCurrentValue: string;

  /**
   * The options for the "After" combo-box
   */
  @State() filterAfterOptions: ComboBoxModel;

  /**
   * Image data to be displayed in the tabular grid (filtered from server and sorted)
   */
  @State() tabularGridData: ImageData[] = [];

  /**
   * Images rendered in the table
   */
  @State() images: ImageData[] = [];

  /**
   * Items of the selected image
   */
  @State() imagesSelectedItems: ImageItemData[] = [];

  /**
   * Selected images in the images table
   */
  @State() selectedObjectsIds: string[] = [];

  /**
   * When true, modified controls will be enabled, otherwise disabled.
   */
  @State() enabledModifiedControls: boolean = false;

  /**
   * Sorting order for images table
   */
  @State() order: { orderCriteria: string | null; ascending: boolean | null } =
    {
      orderCriteria: null,
      ascending: null
    };

  /**
   * Filter state variables
   */
  @State() filterNameValue = "";
  @State() filterSearchContentsValue = "";
  @State() filterCategoryValue: string;
  @State() filterAllDescendantsValue = false;
  @State() filterAfterTypeValue: string;
  @State() filterUserValue = "";
  @State() filterModifiedDateValue = "";
  @State() filterStyleValue: string;
  @State() filterLanguageValue: string;
  @State() filterDensityValue: string;
  @State() filterLayerValue: string;
  @State() filterModuleValue: EntityData;

  /**
   * Flag to prevent triggering #getImages on initial load
   */
  #isInitialLoad = true;

  /**
   * Categories rendered in the filter selector
   */
  @Prop() readonly categories!: GxOption[];
  @Watch("categories")
  categoriesChanged(newCategories: GxOption[]) {
    this.categoriesComboBoxModel = mapOptionsToComboBoxItemModel(newCategories);
    this.#applyDefaultCategory();
  }

  /**
   * Function to expand data when right-click
   */
  @Prop() readonly contextMenuCallback!: ContextMenuCallback;

  /**
   * Default value for the 'categories' filter
   */
  @Prop() readonly defaultCategory: string;
  @Watch("defaultCategory")
  defaultCategoryChanged() {
    this.#applyDefaultCategory();
  }

  /**
   * Default value for the module/folder filter
   */
  @Prop() readonly defaultModule: EntityData;

  /**
   * Function to delete the selected images.
   */
  @Prop() readonly deleteSelectionCallback!: DeleteSelectionCallback;

  /**
   * Densities rendered in the filter selector
   */
  @Prop() readonly densities!: GxOption[];
  @Watch("densities")
  densitiesChanged(newDensities: GxOption[]) {
    this.densitiesComboBoxModel = mapOptionsToComboBoxItemModel(newDensities);
  }

  /**
   * Languages rendered in the filter selector
   */
  @Prop() readonly languages!: GxOption[];
  @Watch("languages")
  languagesChanged(newLanguages: GxOption[]) {
    this.languagesComboBoxModel = mapOptionsToComboBoxItemModel(newLanguages);
  }

  /**
   * Layers rendered in the filter selector
   */
  @Prop() readonly layers!: GxOption[];
  @Watch("layers")
  layersChanged(newLayers: GxOption[]) {
    this.layersComboBoxModel = mapOptionsToComboBoxItemModel(newLayers);
  }

  /**
   * Function that returns a list of images for the table
   */
  @Prop() readonly loadCallback!: LoadCallback;

  /**
   * Function that returns a list of items for the selected image
   */
  @Prop() readonly loadImageItemsCallback!: LoadImageItemsCallback;

  /**
   * Callback invoked when user press New button.
   */
  @Prop() readonly newObjectCallback!: NewObjectCallback;

  /**
   * Function to expand data for double-clicked or entered images
   */
  @Prop() readonly openSelectionCallback!: OpenSelectionCallback;

  /**
   * Callback invoked when an action is executed on the Module/Folder filter.
   */
  @Prop() readonly selectModuleCallback!: SelectModuleCallback;

  /**
   * Callback invoked when an item is selected
   */
  @Prop() readonly selectionObjectChangedCallback!: SelectionObjectCallback;

  /**
   * Styles rendered in the filter style selector
   */
  @Prop() readonly styles!: GxOption[];
  @Watch("styles")
  stylesChanged(newStyles: GxOption[]) {
    this.stylesComboBoxModel = mapOptionsToComboBoxItemModel(newStyles);
  }

  async connectedCallback() {
    TODAY = this.#getToday();
  }

  componentDidLoad() {
    this.#addEventListeners();
    this.#getImages();
    this.#saveInitialFilterData();
    this.#isInitialLoad = false;
  }

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);

    // initialize combo-box models
    this.categoriesChanged(this.categories);
    this.densitiesChanged(this.densities);
    this.languagesChanged(this.languages);
    this.layersChanged(this.layers);
    this.stylesChanged(this.styles);

    // initialize "After" combo-box options
    this.filterAfterOptions = [
      {
        caption: this.#componentLocale.filter.afterNone,
        value: FILTER_AFTER.NONE
      },
      {
        caption: this.#componentLocale.filter[FILTER_AFTER.DATE_TIME],
        value: FILTER_AFTER.DATE_TIME
      },
      {
        caption: this.#componentLocale.filter[FILTER_AFTER.IMPORT],
        value: FILTER_AFTER.IMPORT
      },
      {
        caption: this.#componentLocale.filter[FILTER_AFTER.LAST_BUILD],
        value: FILTER_AFTER.LAST_BUILD
      }
    ];
    this.afterCurrentValue = this.filterAfterOptions[0].value;
    this.filterAfterTypeValue = this.filterAfterOptions[0].value;

    // Initialize filter state values
    this.#applyDefaultCategory();
    this.filterModuleValue = this.defaultModule;
    if (this.stylesComboBoxModel.length > 0) {
      this.filterStyleValue = this.stylesComboBoxModel[0].value;
    }
    if (this.languagesComboBoxModel.length > 0) {
      this.filterLanguageValue = this.languagesComboBoxModel[0].value;
    }
    if (this.densitiesComboBoxModel.length > 0) {
      this.filterDensityValue = this.densitiesComboBoxModel[0].value;
    }
    if (this.layersComboBoxModel.length > 0) {
      this.filterLayerValue = this.layersComboBoxModel[0].value;
    }

    this.#ACCORDION_MODEL = [
      {
        id: this.#ACCORDION_FILTER_ID,
        caption: this.#componentLocale.filter.accordionCaption,
        expanded: false
      }
    ];
  }

  @Listen("keydown", { passive: true })
  keyDownHandler(eventInfo: KeyboardEvent) {
    if (document.activeElement === this.el) {
      switch (eventInfo.key) {
        case "Enter":
          this.#openSelectionCallBackHandler();
          break;
        case "Delete":
          this.#deleteSelectionCallbackHandler();
          break;
      }
    }
  }

  /**
   * Reloads the view, refreshing the filters and the images table.
   */
  @Method()
  async reload(): Promise<void> {
    this.#getImages();
  }

  #deleteSelectionCallbackHandler = (): void => {
    this.deleteSelectionCallback(this.selectedObjectsIds);
  };

  #deselectAll = (): void => {
    (this.#chGridEl as any).selectAllRows(false);
  };

  /**
   * Watch filter state variables and trigger #getImages when they change
   */
  @Watch("filterNameValue")
  filterNameValueChanged() {
    if (!this.#isInitialLoad) {
      this.#getImages();
    }
  }

  @Watch("filterSearchContentsValue")
  filterSearchContentsValueChanged() {
    if (!this.#isInitialLoad) {
      this.#getImages();
    }
  }

  @Watch("filterCategoryValue")
  filterCategoryValueChanged() {
    if (!this.#isInitialLoad) {
      this.#getImages();
    }
  }

  @Watch("filterAllDescendantsValue")
  filterAllDescendantsValueChanged() {
    if (!this.#isInitialLoad) {
      this.#getImages();
    }
  }

  @Watch("filterAfterTypeValue")
  filterAfterTypeValueChanged() {
    if (!this.#isInitialLoad) {
      this.#getImages();
    }
  }

  @Watch("filterUserValue")
  filterUserValueChanged() {
    if (!this.#isInitialLoad) {
      this.#getImages();
    }
  }

  @Watch("filterModifiedDateValue")
  filterModifiedDateValueChanged() {
    if (!this.#isInitialLoad) {
      this.#getImages();
    }
  }

  @Watch("filterStyleValue")
  filterStyleValueChanged() {
    if (!this.#isInitialLoad) {
      this.#getImages();
    }
  }

  @Watch("filterLanguageValue")
  filterLanguageValueChanged() {
    if (!this.#isInitialLoad) {
      this.#getImages();
    }
  }

  @Watch("filterDensityValue")
  filterDensityValueChanged() {
    if (!this.#isInitialLoad) {
      this.#getImages();
    }
  }

  @Watch("filterLayerValue")
  filterLayerValueChanged() {
    if (!this.#isInitialLoad) {
      this.#getImages();
    }
  }

  @Watch("filterModuleValue")
  filterModuleValueChanged() {
    if (!this.#isInitialLoad) {
      this.#getImages();
    }
  }

  /**
   * Individual handler functions for each filter control
   */
  #handleNameChange = (event: CustomEvent<string> | InputEvent) => {
    this.filterNameValue = (event.detail ||
      (event.target as HTMLInputElement).value) as string;
  };

  #handleSearchContentsChange = (event: CustomEvent<string> | InputEvent) => {
    this.filterSearchContentsValue = (event.detail ||
      (event.target as HTMLInputElement).value) as string;
  };

  #applyDefaultCategory = () => {
    if (this.defaultCategory && this.categoriesComboBoxModel.length > 0) {
      const categoryExists = this.categoriesComboBoxModel.some(
        item => item.value === this.defaultCategory
      );
      if (categoryExists) {
        this.filterCategoryValue = this.defaultCategory;
      }
    }
  };

  #handleCategoryChange = (event: CustomEvent<string> | InputEvent) => {
    this.filterCategoryValue = event.detail as string;
  };

  #handleAllDescendantsChange = (event: CustomEvent<string> | InputEvent) => {
    this.filterAllDescendantsValue = event.detail === ALL_DESCENDANTS_VALUE;
  };

  #handleAfterTypeChange = (event: CustomEvent<string> | InputEvent) => {
    this.filterAfterTypeValue = event.detail as string;
    this.afterCurrentValue = event.detail as string;
  };

  #handleUserChange = (event: CustomEvent<string> | InputEvent) => {
    this.filterUserValue = (event.detail ||
      (event.target as HTMLInputElement).value) as string;
  };

  #handleModifiedDateChange = (event: InputEvent) => {
    this.filterModifiedDateValue = (event.target as HTMLInputElement).value;
  };

  #handleStyleChange = (event: CustomEvent<string> | InputEvent) => {
    this.filterStyleValue = event.detail as string;
  };

  #handleLanguageChange = (event: CustomEvent<string> | InputEvent) => {
    this.filterLanguageValue = event.detail as string;
  };

  #handleDensityChange = (event: CustomEvent<string> | InputEvent) => {
    this.filterDensityValue = event.detail as string;
  };

  #handleLayerChange = (event: CustomEvent<string> | InputEvent) => {
    this.filterLayerValue = event.detail as string;
  };

  #handleModuleChange = () => {
    // Module change is handled via event listener, but we update state
    if (this.#filterModuleEl) {
      this.filterModuleValue = this.#filterModuleEl.value;
    }
  };

  #filterImagesInformationHandler = () => {
    // Copy images from server to tabularGridData (will be sorted before rendering)
    this.tabularGridData = [...this.images];
  };

  #getImages = (): void => {
    this.#imagesMap.clear();

    const filters: ImageFiltersData = {
      name: this.filterNameValue,
      searchContents: this.filterSearchContentsValue,
      category: this.filterCategoryValue,
      parent: this.filterModuleValue ? this.filterModuleValue.id : null,
      allDescendants: this.filterAllDescendantsValue,
      modifiedAfter: this.filterAfterTypeValue,
      modifiedBy: this.filterUserValue,
      modifiedDate: this.filterModifiedDateValue
        ? new Date(this.filterModifiedDateValue)
        : undefined,
      style: this.filterStyleValue,
      language: this.filterLanguageValue,
      density: this.filterDensityValue,
      layer: this.filterLayerValue
    };

    this.loadCallback(filters).then((items: ImageData[]) => {
      this.images = items;
      this.selectedObjectsIds = [];
      this.#deselectAll();
      this.#filterImagesInformationHandler();
    });
  };

  #getToday = (): string => {
    const now = new Date();
    const date = now.toISOString().split("T")[0];
    const time = now.toISOString().split("T")[1].slice(0, 5); // Only take 'HH:MM' part
    return `${date}T${time}`;
  };
  #addEventListeners = (): void => {
    /* module/folder */
    this.#filterModuleEl.addEventListener("valueChanged", () => {
      this.#handleModuleChange();
    });

    // for grid selection
    this.#chGridEl.addEventListener("selectionChanged", (ev: any): void => {
      this.selectedObjectsIds = ev.detail.rowsId;
      this.selectionObjectChangedCallback(this.selectedObjectsIds);

      if (this.selectedObjectsIds.length === 0) {
        this.imagesSelectedItems = [];
        return;
      }

      // Collect cached items immediately
      const cachedItems: ImageItemData[] = [];
      const idsToLoad: string[] = [];

      this.selectedObjectsIds.forEach((selectedId: string) => {
        if (this.#imagesMap.has(selectedId)) {
          const items = this.#imagesMap.get(selectedId);
          if (items) {
            cachedItems.push(...items);
          }
        } else {
          idsToLoad.push(selectedId);
        }
      });

      // If all items were cached, update immediately
      if (idsToLoad.length === 0) {
        this.imagesSelectedItems = cachedItems;
        return;
      }

      // Store current selection to verify later (prevents race conditions)
      const currentSelection = [...this.selectedObjectsIds].sort().join(",");

      // Load missing items and combine with cached ones
      // loadImageItemsCallback must be invoked the first time a user selects an image
      const loadPromises = idsToLoad.map((selectedId: string) =>
        this.loadImageItemsCallback(selectedId).then(
          (items: ImageItemData[]) => {
            // Cache the items for future use
            this.#imagesMap.set(selectedId, items);
            return items;
          }
        )
      );

      Promise.all(loadPromises).then((loadedItemsArrays: ImageItemData[][]) => {
        // Verify that selection hasn't changed while loading
        const currentSelectionAfterLoad = [...this.selectedObjectsIds]
          .sort()
          .join(",");
        if (currentSelection !== currentSelectionAfterLoad) {
          // Selection changed, ignore this result
          return;
        }

        // Combine cached items with newly loaded items
        const allImageItems = [...cachedItems];
        loadedItemsArrays.forEach(items => {
          allImageItems.push(...items);
        });
        this.imagesSelectedItems = allImageItems;
      });
    });

    this.#chGridEl.addEventListener("rowDoubleClicked", (): void => {
      this.#openSelectionCallBackHandler();
    });

    this.#chGridEl.addEventListener(
      "rowContextMenu",
      (ev: CustomEvent<ChGridRowContextMenuEvent>): void => {
        ev.preventDefault();
        ev.stopPropagation();
        this.contextMenuCallback({
          selection: ev.detail.selectedRowsId,
          clientX: ev.detail.clientX,
          clientY: ev.detail.clientY
        });
      }
    );
  };

  /**
   * Helper function to safely get image values for sorting
   */
  #getImageValue(image: ImageData, key: string): string {
    switch (key) {
      case "name":
        return image.name || "";
      case "module":
        return image.module || "";
      case "description":
        return image.description || "";
      case "modifiedDate":
        return image.modifiedDate
          ? formatDate(image.modifiedDate).toLowerCase()
          : "";
      case "lastUser":
        return image.lastUser?.toLowerCase() || "";
      case "importDate":
        return image.importDate
          ? formatDate(image.importDate).toLowerCase()
          : "";
      default:
        return "";
    }
  }

  /**
   * Sorts images based on the current order criteria
   */
  #sortImages = (a: ImageData, b: ImageData): number => {
    if (this.order.orderCriteria) {
      const valueA = this.#getImageValue(
        a,
        this.order.orderCriteria
      ).toUpperCase();
      const valueB = this.#getImageValue(
        b,
        this.order.orderCriteria
      ).toUpperCase();
      if (valueA < valueB) {
        return this.order.ascending ? -1 : 1;
      }
      if (valueA > valueB) {
        return this.order.ascending ? 1 : -1;
      }
    }
    return 0;
  };

  /**
   * Handles column sort changes
   */
  #handleColumnSortChanged = (e: CustomEvent) => {
    const { columnId, sortDirection } = e.detail;
    this.order = {
      orderCriteria: columnId,
      ascending: sortDirection === "asc"
    };
  };

  #newObjectCallbackHandler = (): void => {
    this.newObjectCallback();
  };
  #openSelectionCallBackHandler = (): void => {
    this.openSelectionCallback(this.selectedObjectsIds);
  };

  #toggleModifiedControls = (event: CustomEvent<string> | InputEvent) => {
    this.enabledModifiedControls = event.detail === MODIFIED_VALUE;
  };

  #renderHeader = (): Element[] => {
    return [
      <div
        // name
        class="field field-block name"
      >
        <label class="label" htmlFor="filter-name">
          {this.#componentLocale.filter.name}
        </label>
        <ch-edit
          id="filter-name"
          class="input"
          autoFocus={true}
          value={this.filterNameValue}
          debounce={config.inputDebounce}
          onInput={this.#handleNameChange}
        ></ch-edit>
      </div>,

      <div class="field field-block module-folder">
        <label class="label" htmlFor="module-folder">
          {this.#componentLocale.filter.moduleFolder}
        </label>

        <gx-ide-entity-selector
          // module/folder
          id="module/folder"
          labelPosition="none"
          value={this.filterModuleValue}
          defaultValue={this.defaultModule}
          selectEntityCallback={this.selectModuleCallback}
          ref={(el: HTMLGxIdeEntitySelectorElement) =>
            (this.#filterModuleEl = el as HTMLGxIdeEntitySelectorElement)
          }
        ></gx-ide-entity-selector>
      </div>,

      <ch-accordion-render
        // modified
        class="accordion-outlined advanced-filter-accordion"
        model={this.#ACCORDION_MODEL}
      >
        <div slot={this.#ACCORDION_FILTER_ID} class="spacing-body">
          <div class="main__filter field-group scrollable">
            <div
              // category
              class="field field-block category"
            >
              <label class="label" htmlFor="filter-category">
                {this.#componentLocale.filter.category}
              </label>
              <ch-combo-box-render
                id="filter-category"
                class="combo-box"
                value={this.filterCategoryValue}
                disabled={!this.categories}
                model={this.categoriesComboBoxModel}
                onInput={this.#handleCategoryChange}
              ></ch-combo-box-render>
            </div>

            <div
              // category
              class="field-group options"
            >
              <label class="label" htmlFor="filter-category">
                {this.#componentLocale.filter.options}
              </label>
              <div
                // options
                class="field field-inline"
              >
                <ch-checkbox
                  class="checkbox all-descendants"
                  caption={this.#componentLocale.filter.allDescendants}
                  checkedValue={ALL_DESCENDANTS_VALUE}
                  value={
                    this.filterAllDescendantsValue
                      ? ALL_DESCENDANTS_VALUE
                      : undefined
                  }
                  onInput={this.#handleAllDescendantsChange}
                ></ch-checkbox>

                <ch-checkbox
                  class="checkbox modified"
                  caption={this.#componentLocale.filter.modified}
                  checkedValue={MODIFIED_VALUE}
                  onInput={this.#toggleModifiedControls}
                ></ch-checkbox>
              </div>
            </div>

            <div
              // after
              class="field field-block after-date-time"
            >
              <label
                class={{
                  "label": true,
                  "label--disabled": !this.enabledModifiedControls
                }}
                htmlFor="filter-after-type"
              >
                {this.#componentLocale.filter.afterLabel}
              </label>
              <ch-combo-box-render
                id="filter-after-type"
                class="combo-box"
                value={this.filterAfterTypeValue}
                disabled={!this.enabledModifiedControls}
                model={this.filterAfterOptions}
                onInput={this.#handleAfterTypeChange}
              ></ch-combo-box-render>
            </div>

            <div
              // user
              class="field field-block user"
            >
              <label
                class={{
                  "label": true,
                  "label--disabled": !this.enabledModifiedControls
                }}
                htmlFor="filter-user"
              >
                {this.#componentLocale.filter.user}
              </label>
              <ch-edit
                id="filter-user"
                value={this.filterUserValue}
                class="input"
                disabled={!this.enabledModifiedControls}
                debounce={config.inputDebounce}
                onInput={this.#handleUserChange}
              ></ch-edit>
            </div>

            <div
              // date/time
              class="field field-block date-time"
            >
              <label
                class={{
                  "label": true,
                  "label--disabled":
                    this.afterCurrentValue !== FILTER_AFTER.DATE_TIME
                }}
                htmlFor="modified-date"
              >
                {this.#componentLocale.filter.dateTime}
              </label>
              <input
                type="datetime-local"
                class="input "
                id="modified-date"
                name="modifiedDate"
                value={this.filterModifiedDateValue}
                max={TODAY}
                disabled={this.afterCurrentValue !== FILTER_AFTER.DATE_TIME}
                onInput={this.#handleModifiedDateChange}
              ></input>
            </div>

            <div
              // style
              class="field field-block style"
            >
              <label class="label" htmlFor="filter-style">
                {this.#componentLocale.filter.style}
              </label>
              <ch-combo-box-render
                id="filter-style"
                class="combo-box"
                value={this.filterStyleValue}
                disabled={!this.styles}
                model={this.stylesComboBoxModel}
                onInput={this.#handleStyleChange}
              ></ch-combo-box-render>
            </div>

            <div
              // language
              class="field field-block language"
            >
              <label class="label" htmlFor="filter-language">
                {this.#componentLocale.filter.language}
              </label>
              <ch-combo-box-render
                id="filter-language"
                class="combo-box"
                value={this.filterLanguageValue}
                disabled={!this.languages}
                model={this.languagesComboBoxModel}
                onInput={this.#handleLanguageChange}
              ></ch-combo-box-render>
            </div>

            <div
              // density
              class="field field-block"
            >
              <label class="label" htmlFor="filter-density">
                {this.#componentLocale.filter.density}
              </label>
              <ch-combo-box-render
                id="filter-density"
                class="combo-box"
                value={this.filterDensityValue}
                disabled={!this.densities}
                model={this.densitiesComboBoxModel}
                onInput={this.#handleDensityChange}
              ></ch-combo-box-render>
            </div>

            <div
              // layer
              class="field field-block"
            >
              <label class="label" htmlFor="filter-layer">
                {this.#componentLocale.filter.layer}
              </label>
              <ch-combo-box-render
                id="filter-layer"
                class="combo-box"
                value={this.filterLayerValue}
                disabled={!this.layers}
                model={this.layersComboBoxModel}
                onInput={this.#handleLayerChange}
              ></ch-combo-box-render>
            </div>

            <div
              // search contents
              class="field field-block search-by-contents"
            >
              <label class="label" htmlFor="search-input">
                {this.#componentLocale.filter.search}
              </label>
              <ch-edit
                id="search-input"
                debounce={config.inputDebounce}
                class="input"
                value={this.filterSearchContentsValue}
                placeholder={
                  this.#componentLocale.filter.searchContentsPlaceholder
                }
                onInput={this.#handleSearchContentsChange}
                startImgSrc={SEARCH_ICON}
                type="search"
              ></ch-edit>
            </div>

            <button
              // reset filter button
              class="button-secondary button-icon-and-text button-reset"
              onClick={this.#resetFilterHandler}
            >
              <ch-image class="icon-sm" src={RESET_ICON} type="mask"></ch-image>
              {this.#componentLocale.filter.resetFilters}
            </button>
          </div>
        </div>
      </ch-accordion-render>
    ];
  };

  #renderFilter = (): Element[] => {
    return (
      <div class="sidebar-container scrollable">{this.#renderImages()}</div>
    );
  };

  #renderImages = (): Element => {
    return (
      /* tiles */

      <div class="main__images-container spacing-body-inline">
        {this.imagesSelectedItems.map(item => (
          <figure class="figure">
            <picture class="figure__picture">
              <img class="figure__img" src={item.url} alt={item.name} />
            </picture>
            <figcaption class="figure__figcaption">{item.name}</figcaption>
          </figure>
        ))}
      </div>
    );
  };
  #renderTabularGrid = (): Element => {
    const commonColSize = "minmax(max-content, auto)";

    return (
      <div
        class={{
          "field-group": true,
          "main__grid": true
        }}
      >
        <ch-tabular-grid
          ref={(el: HTMLChTabularGridElement) => (this.#chGridEl = el)}
          rowSelectionMode="multiple"
          class="tabular-grid tabular-grid-ww-images"
          columnResizeMode="splitter"
        >
          <ch-tabular-grid-columnset class="tabular-grid-column-set">
            <ch-tabular-grid-column
              columnId="icon"
              columnName=""
              settingable={false}
              size={config.tabularGrid.colSize.maxContent}
              class="tabular-grid-column"
              resizable={true}
              sortable={false}
            ></ch-tabular-grid-column>
            <ch-tabular-grid-column
              columnId="name"
              columnName={this.#componentLocale.tableHead.name}
              settingable={false}
              class="tabular-grid-column"
              column-name-position="text"
              size={commonColSize}
              resizable={true}
              sortable={true}
              onColumnSortChanged={this.#handleColumnSortChanged}
            ></ch-tabular-grid-column>
            <ch-tabular-grid-column
              columnId="module"
              columnName={this.#componentLocale.tableHead.module}
              settingable={false}
              size={commonColSize}
              class="tabular-grid-column"
              resizable={true}
              sortable={true}
              onColumnSortChanged={this.#handleColumnSortChanged}
            ></ch-tabular-grid-column>
            <ch-tabular-grid-column
              columnId="description"
              columnName={this.#componentLocale.tableHead.description}
              settingable={false}
              size="minmax(300px, 1fr)"
              class="tabular-grid-column"
              resizable={false}
              sortable={false}
            ></ch-tabular-grid-column>
            <ch-tabular-grid-column
              columnId="modifiedDate"
              columnName={this.#componentLocale.tableHead.modifiedDate}
              settingable={false}
              size={commonColSize}
              class="tabular-grid-column"
              resizable={true}
              sortable={true}
              onColumnSortChanged={this.#handleColumnSortChanged}
            ></ch-tabular-grid-column>
            <ch-tabular-grid-column
              columnId="lastUser"
              columnName={this.#componentLocale.tableHead.lastUser}
              settingable={false}
              size={commonColSize}
              class="tabular-grid-column"
              resizable={true}
              sortable={true}
              onColumnSortChanged={this.#handleColumnSortChanged}
            ></ch-tabular-grid-column>
            <ch-tabular-grid-column
              columnId="importDate"
              columnName={this.#componentLocale.tableHead.importDate}
              settingable={false}
              size={commonColSize}
              class="tabular-grid-column"
              resizable={true}
              sortable={true}
              onColumnSortChanged={this.#handleColumnSortChanged}
            ></ch-tabular-grid-column>
          </ch-tabular-grid-columnset>

          {this.tabularGridData.length ? (
            <ch-tabular-grid-rowset class="tabular-grid-rowset">
              {[...this.tabularGridData]
                .sort(this.#sortImages)
                .map((obj: ImageData) => (
                  <ch-tabular-grid-row rowid={obj.id} class="tabular-grid-row">
                    <ch-tabular-grid-cell class="tabular-grid-cell">
                      <ch-image src={obj.icon}></ch-image>
                    </ch-tabular-grid-cell>
                    <ch-tabular-grid-cell class="tabular-grid-cell">
                      {obj.name}
                    </ch-tabular-grid-cell>
                    <ch-tabular-grid-cell class="tabular-grid-cell">
                      {obj.module}
                    </ch-tabular-grid-cell>
                    <ch-tabular-grid-cell class="tabular-grid-cell tabular-grid-cell-description">
                      {obj.description}
                    </ch-tabular-grid-cell>
                    <ch-tabular-grid-cell class="tabular-grid-cell">
                      {formatDate(obj.modifiedDate)}
                    </ch-tabular-grid-cell>
                    <ch-tabular-grid-cell class="tabular-grid-cell">
                      {obj.lastUser}
                    </ch-tabular-grid-cell>
                    <ch-tabular-grid-cell class="tabular-grid-cell">
                      {formatDate(obj.importDate)}
                    </ch-tabular-grid-cell>
                  </ch-tabular-grid-row>
                ))}
            </ch-tabular-grid-rowset>
          ) : (
            <ch-tabular-grid-rowset class="tabular-grid-rowset">
              <ch-tabular-grid-rowset-empty>
                <gx-ide-empty-state
                  isAnimated={false}
                  stateIconSrc={NOTICE_ICON}
                  stateTitle={this.#componentLocale.emptyState.title}
                  key="no-images-empty-state"
                ></gx-ide-empty-state>
              </ch-tabular-grid-rowset-empty>
            </ch-tabular-grid-rowset>
          )}
        </ch-tabular-grid>
      </div>
    );
  };

  #resetFilterHandler = () => {
    if (this.#filterInitialData) {
      this.filterNameValue = this.#filterInitialData.name;
      this.filterModuleValue = this.#filterInitialData.module;
      this.filterSearchContentsValue = this.#filterInitialData.searchContents;
      this.filterCategoryValue = this.#filterInitialData.category;
      this.filterAllDescendantsValue =
        this.#filterInitialData.allDescendants === ALL_DESCENDANTS_VALUE;
      this.filterAfterTypeValue = this.#filterInitialData.modifiedAfter;
      this.filterModifiedDateValue = this.#filterInitialData.modifiedDateTime;
      this.filterUserValue = this.#filterInitialData.modifiedUser;
      this.filterStyleValue = this.#filterInitialData.style;
      this.filterLanguageValue = this.#filterInitialData.language;
      this.filterDensityValue = this.#filterInitialData.density;
      this.filterLayerValue = this.#filterInitialData.layer;
      this.afterCurrentValue = this.filterAfterTypeValue;
    }
  };

  #saveInitialFilterData = () => {
    this.#filterInitialData = {
      name: this.filterNameValue,
      module: this.filterModuleValue,
      searchContents: this.filterSearchContentsValue,
      category: this.filterCategoryValue,
      allDescendants: this.filterAllDescendantsValue
        ? ALL_DESCENDANTS_VALUE
        : "",
      modifiedAfter: this.filterAfterTypeValue,
      modifiedDateTime: this.filterModifiedDateValue,
      modifiedUser: this.filterUserValue,
      style: this.filterStyleValue,
      language: this.filterLanguageValue,
      density: this.filterDensityValue,
      layer: this.filterLayerValue
    };
  };

  #selectAll = (): void => {
    (this.#chGridEl as any).selectAllRows();
  };

  render(): void {
    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>

        <section class="section">
          <header class="header control-header-with-border spacing-body-inline spacing-body-block-start field-group">
            {this.#renderHeader()}
          </header>

          <div class="main">
            {this.#renderTabularGrid()}
            <div class="main__aside field-group">{this.#renderFilter()}</div>
          </div>

          <footer class="dialog-footer-with-border dialog-footer-space-between spacing-body">
            <p
              // matching objects
              class="text-body"
            >
              {`${this.tabularGridData.length} ${
                this.#componentLocale.objCount.matching
              }`}
            </p>

            <div class="buttons-spacer">
              <button
                // select all button
                id="button-select-all"
                class="button-secondary"
                onClick={this.#selectAll}
              >
                {this.#componentLocale.footer.btnSelectAll}
              </button>

              <button
                // new image button
                id="button-new-file"
                class="button-primary button-icon-and-text"
                onClick={this.#newObjectCallbackHandler}
              >
                {this.#componentLocale.footer.btnNew}
              </button>
            </div>
          </footer>
        </section>
      </Host>
    );
  }
}
export type ContextMenuCallback = (
  contextMenuInfo: ContextMenuInfo
) => Promise<void>;

export type DeleteSelectionCallback = (ids: string[]) => Promise<void>;

export type FilerInitialData = {
  name: string;
  module: EntityData;
  searchContents: string;
  category: string;
  allDescendants: string;
  modifiedAfter: string;
  modifiedDateTime: string;
  modifiedUser: string;
  style: string;
  language: string;
  density: string;
  layer: string;
};

export type ImageData = {
  id: string;
  icon: string;
  name: string;
  description: string;
  parent: string;
  module: string;
  modifiedDate?: Date;
  lastUser?: string;
  importDate?: Date;
};

export type ImageFiltersData = {
  name?: string;
  searchContents?: string;
  category?: string;
  parent?: string;
  allDescendants?: boolean;
  modifiedAfter?: string;
  modifiedBy?: string;
  modifiedDate?: Date;
  style?: string;
  language?: string;
  density?: string;
  layer?: string;
};

export type ImageItemData = {
  id: string;
  name: string;
  url: string;
};

export type LoadCallback = (filters: ImageFiltersData) => Promise<ImageData[]>;

export type LoadImageItemsCallback = (id: string) => Promise<ImageItemData[]>;

export type NewObjectCallback = () => Promise<void>;

export type OpenSelectionCallback = (ids: string[]) => Promise<void>;

export type SelectModuleCallback = () => Promise<EntityData | undefined>;

export type SelectionObjectCallback = (ids: string[]) => Promise<void>;
